<template>
    <!-- Lightbox Overlay (teleported to body) -->
    <Teleport to="body">
        <Transition name="lightbox-fade">
            <div
                v-if="isOpen"
                class="gallery-lightbox-overlay"
                @click.self="close"
                @keydown.esc="close"
                @keydown.left="prev"
                @keydown.right="next"
                tabindex="0"
                ref="overlayRef"
            >
                <!-- Close Button -->
                <button class="lightbox-close" @click="close" aria-label="Close">
                    <i class="ri-close-line"></i>
                </button>

                <!-- Previous Button -->
                <button
                    v-if="images.length > 1"
                    class="lightbox-nav lightbox-prev"
                    @click.stop="prev"
                    aria-label="Previous image"
                >
                    <i class="ri-arrow-left-s-line"></i>
                </button>

                <!-- Image Container -->
                <div class="lightbox-content" @click.stop>
                    <Transition :name="slideDirection" mode="out-in">
                        <img
                            :key="currentIndex"
                            :src="currentImage.src"
                            :alt="currentImage.alt"
                            class="lightbox-image"
                            @load="imageLoaded = true"
                        />
                    </Transition>

                    <!-- Caption -->
                    <div class="lightbox-caption" v-if="currentImage.title || currentImage.description || currentImage.credits">
                        <h4 v-if="currentImage.title" class="lightbox-title">{{ currentImage.title }}</h4>
                        <p v-if="currentImage.description" class="lightbox-desc">{{ currentImage.description }}</p>
                        <span v-if="currentImage.credits" class="lightbox-credits">{{ currentImage.credits }}</span>
                    </div>

                    <!-- Counter -->
                    <div class="lightbox-counter" v-if="images.length > 1">
                        {{ currentIndex + 1 }} / {{ images.length }}
                    </div>
                </div>

                <!-- Next Button -->
                <button
                    v-if="images.length > 1"
                    class="lightbox-nav lightbox-next"
                    @click.stop="next"
                    aria-label="Next image"
                >
                    <i class="ri-arrow-right-s-line"></i>
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
export default {
    name: 'GalleryLightbox',

    data() {
        return {
            isOpen: false,
            images: [],
            currentIndex: 0,
            imageLoaded: false,
            slideDirection: 'slide-right',
            // MutationObserver for v-html content
            observer: null
        }
    },

    computed: {
        currentImage() {
            return this.images[this.currentIndex] || { src: '', alt: '', title: '', description: '', credits: '' }
        }
    },

    methods: {
        open(images, startIndex = 0) {
            this.images = images
            this.currentIndex = startIndex
            this.isOpen = true
            this.imageLoaded = false
            // Lock body scroll
            document.body.style.overflow = 'hidden'
            this.$nextTick(() => {
                if (this.$refs.overlayRef) {
                    this.$refs.overlayRef.focus()
                }
            })
        },

        close() {
            this.isOpen = false
            document.body.style.overflow = ''
        },

        next() {
            if (this.images.length <= 1) return
            this.slideDirection = 'slide-right'
            this.imageLoaded = false
            this.currentIndex = (this.currentIndex + 1) % this.images.length
        },

        prev() {
            if (this.images.length <= 1) return
            this.slideDirection = 'slide-left'
            this.imageLoaded = false
            this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
        },

        /**
         * Scan a container element for .tinymce-gallery or #slides divs
         * and transform them into interactive thumbnail grids.
         */
        enhanceGalleries(container) {
            if (!container) return

            const galleryDivs = container.querySelectorAll('.tinymce-gallery, #slides')
            galleryDivs.forEach((gallery) => {
                // Skip if already enhanced
                if (gallery.classList.contains('gallery-enhanced')) return
                gallery.classList.add('gallery-enhanced')

                // Parse images from gallery HTML
                const slides = gallery.querySelectorAll('.slide, [id="slide"]')
                const images = []

                slides.forEach((slide) => {
                    const img = slide.querySelector('img')
                    if (!img) return

                    images.push({
                        src: img.getAttribute('src') || '',
                        alt: img.getAttribute('alt') || '',
                        title: img.getAttribute('title') || slide.querySelector('.title, [id="title"]')?.textContent?.trim() || '',
                        description: img.getAttribute('longdesc') || slide.querySelector('.desc, [id="desc"]')?.textContent?.trim() || '',
                        credits: img.getAttribute('data-credits') || slide.querySelector('.cred, [id="cred"]')?.textContent?.trim() || ''
                    })
                })

                if (images.length === 0) return

                // Build the thumbnail grid
                const gridEl = document.createElement('div')
                gridEl.className = 'cms-gallery-grid'

                images.forEach((imgData, idx) => {
                    const thumb = document.createElement('div')
                    thumb.className = 'cms-gallery-thumb'

                    const imgEl = document.createElement('img')
                    imgEl.src = imgData.src
                    imgEl.alt = imgData.alt || imgData.title || ''
                    imgEl.loading = 'lazy'

                    const overlay = document.createElement('div')
                    overlay.className = 'cms-gallery-thumb-overlay'

                    const icon = document.createElement('i')
                    icon.className = 'ri-zoom-in-line'
                    overlay.appendChild(icon)

                    if (imgData.title) {
                        const titleSpan = document.createElement('span')
                        titleSpan.className = 'cms-gallery-thumb-title'
                        titleSpan.textContent = imgData.title
                        overlay.appendChild(titleSpan)
                    }

                    thumb.appendChild(imgEl)
                    thumb.appendChild(overlay)

                    // Click opens lightbox
                    thumb.addEventListener('click', () => {
                        this.open(images, idx)
                    })

                    gridEl.appendChild(thumb)
                })

                // Replace original gallery HTML with the thumbnail grid
                gallery.innerHTML = ''
                gallery.appendChild(gridEl)
            })
        }
    },

    mounted() {
        // Close on Escape key (global listener)
        this._escHandler = (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close()
            }
        }
        document.addEventListener('keydown', this._escHandler)
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this._escHandler)
        document.body.style.overflow = ''
    }
}
</script>

<style>
/* ============================================================
   Thumbnail Grid Styles (global, not scoped — injected into v-html content)
   ============================================================ */

/* Hide original WTamaso inner elements before enhancement */
.tinymce-gallery:not(.gallery-enhanced) .slide .title,
.tinymce-gallery:not(.gallery-enhanced) .slide .desc,
.tinymce-gallery:not(.gallery-enhanced) .slide .cred,
#slides:not(.gallery-enhanced) [id="slide"] [id="title"],
#slides:not(.gallery-enhanced) [id="slide"] [id="desc"],
#slides:not(.gallery-enhanced) [id="slide"] [id="cred"] {
    display: none;
}

.cms-gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    margin: 30px 0;
}

.cms-gallery-thumb {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    aspect-ratio: 4 / 3;
    background: #f3f4f6;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.cms-gallery-thumb:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.cms-gallery-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
    /* Override any parent scoped styles that might force different sizing */
    border-radius: 0 !important;
    margin: 0 !important;
    max-width: none !important;
}

.cms-gallery-thumb:hover img {
    transform: scale(1.08);
}

.cms-gallery-thumb-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: 16px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.cms-gallery-thumb:hover .cms-gallery-thumb-overlay {
    opacity: 1;
}

.cms-gallery-thumb-overlay i {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    font-size: 32px;
    color: #fff;
    background: rgba(0, 0, 0, 0.4);
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
    backdrop-filter: blur(4px);
}

.cms-gallery-thumb:hover .cms-gallery-thumb-overlay i {
    transform: translate(-50%, -50%) scale(1);
}

.cms-gallery-thumb-title {
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    text-align: center;
    line-height: 1.3;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ============================================================
   Lightbox Overlay Styles
   ============================================================ */

.gallery-lightbox-overlay {
    position: fixed;
    inset: 0;
    z-index: 99999;
    background: rgba(0, 0, 0, 0.92);
    display: flex;
    align-items: center;
    justify-content: center;
    outline: none;
    backdrop-filter: blur(8px);
}

.lightbox-close {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease, transform 0.2s ease;
    backdrop-filter: blur(4px);
}

.lightbox-close:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.1);
}

.lightbox-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(255, 255, 255, 0.12);
    border: none;
    color: #fff;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    font-size: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease, transform 0.2s ease;
    backdrop-filter: blur(4px);
}

.lightbox-nav:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-50%) scale(1.1);
}

.lightbox-prev {
    left: 20px;
}

.lightbox-next {
    right: 20px;
}

.lightbox-content {
    max-width: 85vw;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
}

.lightbox-image {
    max-width: 85vw;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.4);
    user-select: none;
    /* Override parent scoped styles */
    margin: 0 !important;
}

.lightbox-caption {
    text-align: center;
    margin-top: 16px;
    max-width: 600px;
    padding: 0 20px;
}

.lightbox-title {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 6px 0;
    font-family: var(--primaryFont, 'Plus Jakarta Sans', sans-serif);
}

.lightbox-desc {
    color: rgba(255, 255, 255, 0.75);
    font-size: 14px;
    line-height: 1.5;
    margin: 0 0 4px 0;
}

.lightbox-credits {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    font-style: italic;
}

.lightbox-counter {
    position: absolute;
    bottom: -32px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.5px;
}

/* ============================================================
   Transitions
   ============================================================ */

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
    transition: opacity 0.3s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
    opacity: 0;
}

/* Slide transitions for image switching */
.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
    transition: all 0.25s ease;
}

.slide-right-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-right-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-left-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-left-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* ============================================================
   Responsive
   ============================================================ */

@media (max-width: 991px) {
    .cms-gallery-grid {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        gap: 12px;
    }

    .lightbox-nav {
        width: 42px;
        height: 42px;
        font-size: 22px;
    }

    .lightbox-prev {
        left: 10px;
    }

    .lightbox-next {
        right: 10px;
    }
}

@media (max-width: 575px) {
    .cms-gallery-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .cms-gallery-thumb {
        border-radius: 8px;
    }

    .cms-gallery-thumb-overlay i {
        width: 40px;
        height: 40px;
        font-size: 24px;
    }

    .lightbox-image {
        max-width: 95vw;
        max-height: 65vh;
    }

    .lightbox-nav {
        width: 36px;
        height: 36px;
        font-size: 20px;
    }

    .lightbox-prev {
        left: 6px;
    }

    .lightbox-next {
        right: 6px;
    }

    .lightbox-close {
        top: 10px;
        right: 10px;
        width: 36px;
        height: 36px;
        font-size: 20px;
    }
}
</style>
